import React, {useEffect, useState} from "react";
import {Alert, Box, Button, Card, CircularProgress, Grid, Skeleton, Stack, Typography} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
	EmpEvaluationRestControllerApiFactory,
	Evaluation,
	EvaluationPoint,
	EvaluationResult,
	Person,
	SimpleEvaluation,
	User,
} from "arteco-api-client-ts";
import dayjs from "dayjs";
import AdminInner from "../../../components/layout/admin_inner";
import {useApiManager} from "../../../components/utils/api";
import {useAuthManager} from "../../../components/utils/auth";
import {dateToString} from "../../../components/utils/dates";
import {btnSx, panelSx} from "../../admin/employee/interview/ui";
import {
	Answers,
	EvaluationComments,
	EvaluationEditor,
	formatScore,
	ScoreBadge,
	ScoreBar,
	scoreColor,
} from "../../admin/employee/evaluation/ui";

/**
 * Autoevaluación mensual del empleado: las preguntas con su escala, el progreso y los comentarios; al
 * lado, sus autoevaluaciones anteriores. Si ya hizo la del mes, se le dice, pero puede enviar otra.
 */
export const EmployeeEvaluationPage = () => {
	const apiManager = useApiManager();
	const authManager = useAuthManager();
	const ctrl = apiManager.factory(EmpEvaluationRestControllerApiFactory);

	const [points, setPoints] = useState<EvaluationPoint[]>();
	const [results, setResults] = useState<EvaluationResult[]>();
	const [history, setHistory] = useState<SimpleEvaluation[]>();
	const [answers, setAnswers] = useState<Answers>({});
	const [comments, setComments] = useState<EvaluationComments>({});
	const [saving, setSaving] = useState(false);
	const [triedToSend, setTriedToSend] = useState(false);

	useEffect(() => {
		apiManager.execute(ctrl.getEvaluationPoints())
			.then(list => setPoints([...list].sort((a, b) => a.position - b.position))).catch(() => setPoints([]));
		apiManager.execute(ctrl.getPointResults()).then(setResults).catch(() => setResults([]));
		apiManager.execute(ctrl.pageEmployeeEvaluation(0, 24))
			.then(p => setHistory(p.content || [])).catch(() => setHistory([]));
	}, []);

	const answered = (points || []).filter(p => answers[String(p.id)]).length;
	const complete = !!points && points.length > 0 && answered === points.length;
	const thisMonth = (history || []).find(h => dayjs(h.evaluationDate).isSame(dayjs(), "month"));

	const send = () => {
		setTriedToSend(true);
		if (!complete) {
			return;
		}
		setSaving(true);
		const pts: { [k: string]: EvaluationResult } = {};
		Object.entries(answers).forEach(([k, v]) => v && (pts[k] = {id: v.id} as EvaluationResult));
		const body = {
			...comments,
			person: {id: -1} as Person,              // El API pone la persona de la sesión.
			modifier: {id: authManager.getUserId()} as User,
			evaluationDate: dayjs().format("YYYY-MM-DD"),
			points: pts,
		} as Evaluation;
		apiManager.execute(ctrl.saveEmployeeEvaluation(body), "Autoevaluación enviada. ¡Gracias!")
			// Recarga completa: el menú bloquea el resto del portal mientras falta la del mes.
			.then(() => window.location.href = "/app/employee")
			.catch(() => setSaving(false));
	};

	const loading = !points || !results;

	return (
		<AdminInner withoutPaper>
			<Box sx={{maxWidth: 1180}}>
				<Box sx={{mb: 3}}>
					<Typography variant="h5" component="h1" fontWeight={700}>Autoevaluación</Typography>
					<Typography variant="body2" color="text.secondary">
						Una vez al mes, unos minutos: cómo estás en tu trabajo, tu equipo y la empresa. La lee RR. HH.
					</Typography>
				</Box>

				{thisMonth && (
					<Alert icon={<CheckCircleRoundedIcon/>} severity="success" sx={{mb: 3, borderRadius: 2.5}}>
						Ya hiciste la de {dayjs().format("MMMM")} el {dateToString(thisMonth.evaluationDate)}. Si algo ha cambiado, puedes enviar otra.
					</Alert>
				)}

				<Grid container spacing={3}>
					<Grid item xs={12} md={8}>
						{loading ? <Skeleton variant="rounded" height={600}/> : (
							<Stack spacing={3}>
								<EvaluationEditor points={points} results={results} answers={answers} comments={comments}
												  commentsHint="Opcionales. Lo que quieras contar con tus palabras; una vez enviados no se pueden cambiar."
												  onAnswer={(pid, r) => setAnswers(a => ({...a, [String(pid)]: r}))}
												  onComment={(f, v) => setComments(c => ({...c, [f]: v}))}/>

								<Card elevation={0} sx={{...panelSx, position: {xs: "sticky", md: "static"}, bottom: {xs: 8}, zIndex: 2}}>
									<Stack direction={{xs: "column", sm: "row"}} spacing={2} alignItems={{sm: "center"}} justifyContent="space-between">
										<Box>
											<Typography variant="body2" fontWeight={600}>
												{complete ? "Todo respondido" : `Faltan ${points.length - answered} de ${points.length} preguntas`}
											</Typography>
											{triedToSend && !complete && (
												<Typography variant="caption" color="error.main">Responde todas las preguntas para enviarla.</Typography>
											)}
										</Box>
										<Button variant="contained" disableElevation size="large" onClick={send} disabled={saving}
												sx={{...btnSx, px: 4, py: 1.25}}>
											{saving ? <CircularProgress size={22} color="inherit"/> : "Enviar autoevaluación"}
										</Button>
									</Stack>
								</Card>
							</Stack>
						)}
					</Grid>

					<Grid item xs={12} md={4}>
						<Stack spacing={3} sx={{position: {md: "sticky"}, top: {md: 16}}}>
							<Card elevation={0} sx={panelSx}>
								<Typography variant="subtitle1" fontWeight={700}>Tus anteriores</Typography>
								{!history ? <Skeleton variant="rounded" height={80} sx={{mt: 2}}/> : history.length === 0 ? (
									<Typography variant="body2" color="text.secondary" sx={{mt: 1}}>Esta es la primera.</Typography>
								) : (
									<Stack spacing={1.25} sx={{mt: 2}}>
										{history.slice(0, 12).map((h, i) => (
											<Stack key={i} direction="row" spacing={1.5} alignItems="center">
												<Typography variant="body2" fontWeight={600} sx={{width: 92, fontVariantNumeric: "tabular-nums"}}>
													{dateToString(h.evaluationDate)}
												</Typography>
												<Box sx={{flexGrow: 1}}><ScoreBar rating={h.average}/></Box>
												<Typography variant="body2" fontWeight={700} sx={{width: 28, textAlign: "right", color: scoreColor(h.average)}}>
													{formatScore(h.average)}
												</Typography>
											</Stack>
										))}
									</Stack>
								)}
								{history && history.length > 0 && (
									<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mt: 2.5}}>
										<Typography variant="body2" color="text.secondary">La última</Typography>
										<ScoreBadge rating={history[0].average}/>
									</Stack>
								)}
							</Card>
							<Stack direction="row" spacing={1} sx={{color: "text.secondary", px: 1}}>
								<LockOutlinedIcon sx={{fontSize: 18, mt: "2px"}}/>
								<Typography variant="caption">
									Solo la ven RR. HH. y dirección. Sirve para detectar a tiempo lo que no va bien; si quieres
									hablarlo en persona, dilo en los comentarios.
								</Typography>
							</Stack>
						</Stack>
					</Grid>
				</Grid>
			</Box>
		</AdminInner>
	);
};
